﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import * as React from "react"

import { ExportButton } from "@/features/exports/export-button"
import { DataTable, useListParams, type Column } from "@/components/data-table"
import { Badge, Button, PageHeader } from "@/components/ui"
import { useProviders, type Provider, type ProviderOrdering } from "@/features/providers/api"
import { CreateProviderDialog } from "@/features/providers/create-provider-dialog"
import { PAGE_SIZE, STATUS_LABELS, STATUS_TONES } from "@/lib/constants"
import { useSession } from "@/lib/session"

export const Route = createFileRoute("/providers/")({ component: ProvidersScreen })

/** An empty cell reads as empty to a screen reader; the dash is for the eye. */
function orDash(value: string | null | undefined) {
  if (value) return value
  return (
    <span className="text-muted-foreground" aria-hidden>
      &ndash;
    </span>
  )
}

function ProvidersScreen() {
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const [creating, setCreating] = React.useState(false)

  const { page, setPage, ordering, setOrdering, search, setSearch, debouncedSearch } =
    useListParams<ProviderOrdering>("name")

  const query = useProviders({ page, q: debouncedSearch, ordering })

  function openProvider(id: number | null | undefined) {
    if (id === null || id === undefined) return
    void navigate({
      to: "/providers/$providerId",
      params: { providerId: String(id) },
    })
  }

  // Sortable exactly where the endpoint's ordering union allows it: compliance
  // rating is free text on the server and is not one of them.
  const columns: Column<Provider>[] = [
    {
      key: "name",
      header: "Provider",
      sortable: true,
      primary: true,
      cell: (provider) => <span className="font-medium">{provider.name}</span>,
    },
    {
      key: "location",
      header: "Location",
      sortable: true,
      cell: (provider) => orDash(provider.location),
    },
    {
      key: "company_type",
      header: "Company type",
      sortable: true,
      cell: (provider) => orDash(provider.company_type),
    },
    {
      key: "bbbee_level",
      header: "BBBEE level",
      sortable: true,
      cell: (provider) => orDash(provider.bbbee_level),
    },
    {
      key: "compliance_rating",
      header: "Compliance rating",
      cell: (provider) => orDash(provider.compliance_rating),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      cell: (provider) => (
        <Badge tone={STATUS_TONES[provider.status] ?? "neutral"}>
          {STATUS_LABELS[provider.status] ?? provider.status}
        </Badge>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Libraries"
        title="Providers"
        description="The roster of external and in-house providers that deliver training."
        actions={
          <div className="flex items-start gap-2">
            <ExportButton
              slug="providers"
              params={{ q: debouncedSearch, ordering }}
            />
            {canWrite && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                New provider
              </Button>
            )}
          </div>
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        count={query.data?.count ?? 0}
        rowKey={(provider) => provider.id ?? provider.name}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        ordering={ordering}
        onOrderingChange={setOrdering}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search providers by name"
        isLoading={query.isLoading}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(provider) => openProvider(provider.id)}
        emptyTitle={debouncedSearch ? "No providers match that search" : "No providers yet"}
        emptyDescription={
          debouncedSearch
            ? "Try a shorter search term, or clear it to see the whole list."
            : "Providers are the organisations that deliver your training events."
        }
        emptyAction={
          canWrite && !debouncedSearch ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              New provider
            </Button>
          ) : undefined
        }
      />

      {creating && (
        <CreateProviderDialog
          onClose={() => setCreating(false)}
          onCreated={(provider) => {
            setCreating(false)
            openProvider(provider.id)
          }}
        />
      )}
    </>
  )
}
